import TranslationHistory from '../../components/apple-ui/TranslationHistory';
import React, { useState, useEffect, useMemo } from 'react';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple } from '@/components/apple-ui';
import {
  Download, RefreshCw, Search, Filter, Languages, FileText, ClipboardCheck,
  FileUp, FileDown, ChevronLeft, ChevronRight, Clock, FileType, Layers, Trash2, MoreVertical, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '@/styles/apple-design-system.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Activity type configurations - matching New UI exactly
const ACTIVITY_CONFIG = {
  basic_translation: { icon: Languages, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700', label: 'Basic Translation' },
  cat_translation: { icon: FileText, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700', label: 'CAT Translation' },
  cat_export: { icon: FileDown, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700', label: 'CAT Export' },
  mtpe_refinement: { icon: FileText, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', label: 'MTPE Refinement' },
  mtpe_batch: { icon: Layers, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', label: 'MTPE Batch' },
  mtpe_pro: { icon: Layers, color: 'violet', bgColor: 'bg-violet-50', textColor: 'text-violet-700', label: 'MTPE Pro' },
  tqa_report: { icon: ClipboardCheck, color: 'orange', bgColor: 'bg-amber-50', textColor: 'text-amber-700', label: 'TQA Report' },
  review_proofread: { icon: ClipboardCheck, color: 'rose', bgColor: 'bg-rose-50', textColor: 'text-rose-700', label: 'Proofreading' },
  super_review: { icon: ClipboardCheck, color: 'violet', bgColor: 'bg-violet-50', textColor: 'text-violet-700', label: 'Super Review' },
  tmx_import: { icon: FileUp, color: 'blue', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700', label: 'TMX Import' },
  tmx_export: { icon: FileDown, color: 'blue', bgColor: 'bg-sky-50', textColor: 'text-sky-700', label: 'TMX Export' }
};

const ACTIVITY_TYPES = [
  { value: 'all', label: 'All Activities' },
  { value: 'basic_translation', label: 'Basic Translation' },
  { value: 'cat_translation', label: 'CAT Translation' },
  { value: 'cat_export', label: 'CAT Export' },
  { value: 'mtpe_refinement', label: 'MTPE Refinement' },
  { value: 'mtpe_batch', label: 'MTPE Batch' },
  { value: 'mtpe_pro', label: 'MTPE Pro' },
  { value: 'tqa_report', label: 'TQA Report' },
  { value: 'review_proofread', label: 'Proofreading' },
  { value: 'super_review', label: 'Super Review' },
  { value: 'tmx_import', label: 'TMX Import' },
  { value: 'tmx_export', label: 'TMX Export' }
];

const ActivityPageApple = ({ user, planInfo, onLogout }) => {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [menuOpen, setMenuOpen] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Translation History Panel (for basic_translation filter)
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [translationHistory, setTranslationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 10;
  
  const limit = 12;
  const token = localStorage.getItem('token');
  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const fetchActivities = async (pageNum = 0) => {
    setFetchError(null);
    setLoading(true);
    try {
      let url = `${API}/api/activity/list?limit=${limit}&offset=${pageNum * limit}`;
      if (typeFilter !== 'all') url += `&activity_type=${typeFilter}`;
      const response = await axios.get(url, axiosConfig);
      setActivities(response.data.activities || []);
      setHasMore(response.data.has_more || false);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      setFetchError('Failed to fetch activity history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/activity/summary`, axiosConfig);
      setSummary(response.data.summary || null);
    } catch (error) { console.error('Failed to fetch summary:', error); }
  };

  useEffect(() => { fetchActivities(page); }, [page, typeFilter]);
  useEffect(() => { fetchSummary(); }, []);
  useEffect(() => { setPage(0); }, [typeFilter]);
  
  // Show history panel when filter is basic_translation only
  // On 'all', translation history is merged inline
  useEffect(() => {
    if (typeFilter === 'basic_translation') {
      setShowHistoryPanel(true);
      fetchTranslationHistory();
    } else if (typeFilter === 'all') {
      setShowHistoryPanel(false);
      fetchTranslationHistory(); // fetch for inline merge
    } else {
      setShowHistoryPanel(false);
      setTranslationHistory([]);
    }
  }, [typeFilter]);

  // Fetch translation history
  const fetchTranslationHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/api/translation/history`, axiosConfig);
      setTranslationHistory(response.data.history || []);
    } catch (error) { 
      console.error('Failed to load translation history:', error);
      setTranslationHistory([]);
    }
    finally { setHistoryLoading(false); }
  };

  // Delete translation history item
  const handleDeleteHistoryItem = async (id) => {
    if (!window.confirm('Delete this translation from history?')) return;
    
    try {
      const response = await axios.delete(`${API}/api/translation/history/${id}`, axiosConfig);
      if (response.status === 200) {
        setTranslationHistory(prev => prev.filter(item => (item._id || item.id) !== id));
        toast.success('Deleted from history');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Format relative time for history items
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get language label
  const getLanguageLabel = (code) => {
    const labels = {
      'en': 'English', 'hi': 'Hindi', 'gu': 'Gujarati', 'ta': 'Tamil', 'te': 'Telugu',
      'pa': 'Punjabi', 'mr': 'Marathi', 'kn': 'Kannada', 'ml': 'Malayalam', 'bn': 'Bengali',
      'or': 'Odia', 'as': 'Assamese', 'ur': 'Urdu', 'de': 'German', 'fr': 'French', 'es': 'Spanish'
    };
    return labels[code] || code?.toUpperCase() || '??';
  };

  // History pagination
  const totalHistoryPages = Math.ceil(translationHistory.length / HISTORY_PER_PAGE);
  const paginatedHistory = translationHistory.slice(
    (historyPage - 1) * HISTORY_PER_PAGE,
    historyPage * HISTORY_PER_PAGE
  );

  // Download handlers - matching New UI exactly
  const handleDownload = async (activity) => {
    try {
      if (activity.activity_type === 'cat_translation' || activity.activity_type === 'cat_export') {
        if (activity.download_id) {
          const response = await axios.get(`${API}/api/cat/history/${activity.download_id}/download`, { ...axiosConfig, responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', activity.file_name || 'translation.docx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Download started');
        }
      } else if (activity.activity_type === 'tqa_report' && activity.download_id) {
        const response = await axios.get(`${API}/api/tqa/download/${activity.download_id}`, { ...axiosConfig, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', activity.file_name || 'TQA_Report.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Download started');
      } else if (activity.activity_type === 'mtpe_batch' && activity.download_id) {
        const response = await axios.get(`${API}/api/mtpe/batch/download/${activity.download_id}`, { ...axiosConfig, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', activity.file_name || 'mtpe_batch.zip');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Download started');
      } else if (activity.download_url) {
        window.open(activity.download_url, '_blank');
        toast.success('Download started');
      } else {
        toast.info('No downloadable file available');
      }
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (activityId) => {
    try {
      await axios.delete(`${API}/api/activity/${activityId}`, axiosConfig);
      toast.success('Activity removed');
      fetchActivities(page);
      fetchSummary();
    } catch (error) { toast.error('Failed to delete activity'); }
    setMenuOpen(null);
  };

  // Client-side search filtering - matching New UI
  const filteredActivities = useMemo(() => {
    if (!searchFilter) return activities;
    const searchStr = searchFilter.toLowerCase();
    return activities.filter(activity =>
      (activity.title || '').toLowerCase().includes(searchStr) ||
      (activity.description || '').toLowerCase().includes(searchStr) ||
      (activity.file_name || '').toLowerCase().includes(searchStr) ||
      (activity.project_name || '').toLowerCase().includes(searchStr) ||
      (activity.source_lang || '').toLowerCase().includes(searchStr) ||
      (activity.target_lang || '').toLowerCase().includes(searchStr)
    );
  }, [activities, searchFilter]);

  const summaryTotals = summary?.totals || { total_activities: 0, total_words: 0, total_segments: 0 };
  const recentCount = summary?.recent_7_days || 0;

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-activity-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="apple-content-title">Activity History</h1>
            <p className="apple-content-subtitle">Track all your translation activities</p>
          </div>
          <ButtonApple variant="ghost" icon={RefreshCw} onClick={() => { fetchActivities(page); fetchSummary(); }} disabled={loading} data-testid="refresh-button">
            Refresh
          </ButtonApple>
        </div>

        {/* Summary Cards - matching New UI gradient style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 p-5 text-white" data-testid="summary-total-activities">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Total Activities</p>
                <p className="text-3xl font-bold mt-1">{summaryTotals.total_activities.toLocaleString()}</p>
                <p className="text-xs opacity-75 mt-1">All time</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl"><FileText size={24} /></div>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-5 text-white" data-testid="summary-words-processed">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Words Processed</p>
                <p className="text-3xl font-bold mt-1">{summaryTotals.total_words.toLocaleString()}</p>
                <p className="text-xs opacity-75 mt-1">Across all modules</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl"><Languages size={24} /></div>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 p-5 text-white" data-testid="summary-segments">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Segments</p>
                <p className="text-3xl font-bold mt-1">{summaryTotals.total_segments.toLocaleString()}</p>
                <p className="text-xs opacity-75 mt-1">Total translated</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl"><Layers size={24} /></div>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 p-5 text-white" data-testid="summary-this-week">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">This Week</p>
                <p className="text-3xl font-bold mt-1">{recentCount.toLocaleString()}</p>
                <p className="text-xs opacity-75 mt-1">Last 7 days</p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl"><Clock size={24} /></div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <CardApple className="mb-6" data-testid="filters-section">
          <CardApple.Body>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative flex-1 w-full max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="text" placeholder="Search activities..." value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} className="apple-input pl-11" data-testid="search-input" />
              </div>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="apple-input apple-select w-full sm:w-52" data-testid="type-filter">
                {ACTIVITY_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
            </div>
            <p className="text-sm text-gray-500 mt-3 pt-3 border-t border-gray-100" data-testid="results-count">
              Showing {filteredActivities.length} of {totalCount} activities
            </p>
          </CardApple.Body>
        </CardApple>

        {/* Activity Cards Grid */}
        {typeFilter === 'basic_translation' ? (
          <TranslationHistory
            history={translationHistory}
            loading={historyLoading}
            onReuse={() => {}}
            onDelete={(id) => setTranslationHistory(prev => prev.filter(item => (item._id || item.id) !== id))}
            onRefresh={fetchTranslationHistory}
          />
        ) : loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center rounded-2xl border border-gray-200 bg-white" data-testid="error-state">
            <AlertTriangle size={48} className="text-amber-500" />
            <p className="text-base font-medium text-gray-700">{fetchError}</p>
            <button
              onClick={() => fetchActivities(page)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
            >
              <RefreshCw size={15} /> Try Again
            </button>
          </div>
        ) : filteredActivities.length === 0 && !(typeFilter === 'all' && translationHistory.length > 0) ? (
          <CardApple data-testid="empty-state">
            <CardApple.Body className="py-16 text-center">
              <FileText className="mx-auto mb-4 text-gray-300" size={64} />
              <p className="font-medium text-lg text-gray-900 mb-1">No activities found</p>
              <p className="text-sm text-gray-500">{searchFilter || typeFilter !== 'all' ? 'Try adjusting your filters' : 'Your translation activities will appear here'}</p>
              {(searchFilter || typeFilter !== 'all') && (
                <ButtonApple variant="ghost" size="sm" onClick={() => { setSearchFilter(''); setTypeFilter('all'); }} className="mt-4">
                  Clear filters
                </ButtonApple>
              )}
            </CardApple.Body>
          </CardApple>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="activities-grid">
            {filteredActivities.map((activity) => (
              <ActivityCardApple
                key={activity.id}
                activity={activity}
                isMenuOpen={menuOpen === activity.id}
                onMenuToggle={() => setMenuOpen(menuOpen === activity.id ? null : activity.id)}
                onDownload={() => handleDownload(activity)}
                onDelete={() => handleDelete(activity.id)}
              />
            ))}
            {/* Inline Basic Translation History cards when 'all' filter */}
            {typeFilter === 'all' && translationHistory.map((item, idx) => {
              const sourceLang = item.source_language || item.source_lang || '';
              const targetLang = item.target_language || item.target_lang || '';
              const targetText = item.translated_text || item.target_text || '';
              return (
                <div key={item._id || item.id || idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">Basic Translation</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {(item.timestamp || item.created_at) ? new Date(item.timestamp || item.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <span className="font-medium text-gray-700">{getLanguageLabel(sourceLang)}</span>
                    <span>→</span>
                    <span className="font-medium text-gray-700">{getLanguageLabel(targetLang)}</span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Source</p>
                    <div className="text-sm bg-gray-50 rounded p-2 max-h-16 overflow-y-auto border border-gray-100">{item.source_text}</div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Translation</p>
                    <div className="text-sm bg-amber-50 rounded p-2 max-h-16 overflow-y-auto border border-amber-100">{targetText}</div>
                  </div>
                  <div className="flex items-center justify-between mt-1 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {item.word_count && <span>{item.word_count} words</span>}
                      {item.is_tm_match && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">TM Match</span>}
                    </div>
                    <button
                      onClick={() => { if(window.confirm('Delete?')) { fetch(`${API}/api/translation/history/${item._id || item.id}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }).then(() => setTranslationHistory(prev => prev.filter(h => (h._id || h.id) !== (item._id || item.id)))); } }}
                      className="text-xs text-red-400 hover:text-red-600"
                    >Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {typeFilter !== 'basic_translation' && totalCount > limit && (
          <div className="flex items-center justify-between mt-8" data-testid="pagination">
            <p className="text-sm text-gray-500">Page {page + 1} of {Math.ceil(totalCount / limit)}</p>
            <div className="flex gap-2">
              <ButtonApple variant="secondary" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0 || loading} icon={ChevronLeft} data-testid="prev-page-button">
                Previous
              </ButtonApple>
              <ButtonApple variant="secondary" size="sm" onClick={() => setPage(page + 1)} disabled={!hasMore || loading} icon={ChevronRight} iconPosition="right" data-testid="next-page-button">
                Next
              </ButtonApple>
            </div>
          </div>
        )}

        {/* Retention Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">Activity history is retained indefinitely. Contact support for data management requests.</p>
        </div>

        {menuOpen && <div className="fixed inset-0 z-5" onClick={() => setMenuOpen(null)} />}
      </div>
    </MainLayoutApple>
  );
};

const getActivityTimeAgo = (date) => {
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' });
};

const getActivityFormattedDate = (date) => date.toLocaleString('en-IN', {
  month: 'short', day: 'numeric', year: 'numeric',
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata'
}) + ' IST';

// Activity Card Component - Apple UI Style with full feature parity
const ActivityCardApple = ({ activity, isMenuOpen, onMenuToggle, onDownload, onDelete }) => {
  const config = ACTIVITY_CONFIG[activity.activity_type] || { icon: FileText, color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-700', label: activity.activity_type };
  const Icon = config.icon;
  const hasDownload = activity.download_url || activity.download_id;

  const createdAt = activity.created_at ? new Date(activity.created_at) : new Date();
  const timeAgo = getActivityTimeAgo(createdAt);
  const formattedDate = getActivityFormattedDate(createdAt);

  return (
    <CardApple className="group relative overflow-hidden" data-testid={`activity-card-${activity.id}`}>
      {/* Gradient accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${config.color}-500 to-${config.color}-600`} />
      
      <CardApple.Body className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${config.bgColor}`}>
              <Icon className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <div>
              <BadgeApple variant={config.color} size="sm">{config.label}</BadgeApple>
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Clock size={12} />{timeAgo}
              </p>
            </div>
          </div>
          
          {/* Actions dropdown */}
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); onMenuToggle(); }} className="h-8 w-8 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`activity-menu-${activity.id}`}>
              <MoreVertical size={16} />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50">
                {hasDownload && (
                  <button onClick={(e) => { e.stopPropagation(); onDownload(); }} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-testid={`download-action-${activity.id}`}>
                    <Download size={14} />Download
                  </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50" data-testid={`delete-action-${activity.id}`}>
                  <Trash2 size={14} />Delete
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Title */}
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2" title={activity.title}>
          {activity.title || activity.file_name || config.label}
        </h3>
        
        {/* Description */}
        {activity.description && (
          <p className="text-sm text-gray-500 mb-3 line-clamp-2">{activity.description}</p>
        )}
        
        {/* Meta Badges */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {activity.source_lang && activity.target_lang && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">{activity.source_lang} → {activity.target_lang}</span>
          )}
          {activity.word_count > 0 && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">{activity.word_count.toLocaleString()} words</span>
          )}
          {activity.segment_count > 0 && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">{activity.segment_count} segments</span>
          )}
          {activity.file_name && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full flex items-center gap-1">
              <FileType size={10} />
              {activity.file_name.length > 20 ? activity.file_name.substring(0, 17) + '...' : activity.file_name}
            </span>
          )}
        </div>
        
        {/* Project info */}
        {activity.project_name && (
          <div className="text-xs text-gray-400 mb-3">
            Project: <span className="text-gray-600">{activity.project_name}</span>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">{formattedDate}</span>
          {hasDownload && (
            <ButtonApple variant="secondary" size="sm" icon={Download} onClick={onDownload} data-testid={`download-btn-${activity.id}`}>
              Download
            </ButtonApple>
          )}
        </div>
      </CardApple.Body>
    </CardApple>
  );
};

export default ActivityPageApple;
